<template>
    <div class="propose-picker">
        <section class="propose-picker__calendar" :aria-label="$t('pages.referrals.pool.propose_picker.choose_times')">
            <div class="propose-picker__week">
                <button
                    type="button"
                    class="propose-picker__week-btn"
                    :disabled="weekIndex === 0"
                    :aria-label="$t('pages.referrals.pool.propose_picker.previous_week')"
                    @click="changeWeek(-1)"
                >
                    ‹
                </button>
                <div class="propose-picker__week-label">{{ weekLabel }}</div>
                <button
                    type="button"
                    class="propose-picker__week-btn"
                    :disabled="weekIndex >= maxWeeks - 1"
                    :aria-label="$t('pages.referrals.pool.propose_picker.next_week')"
                    @click="changeWeek(1)"
                >
                    ›
                </button>
            </div>

            <div class="propose-picker__days">
                <button
                    v-for="day in weekDays"
                    :key="day.key"
                    type="button"
                    :class="dayClasses(day)"
                    :disabled="!day.isOpen"
                    :aria-pressed="day.isSelected ? 'true' : 'false'"
                    :aria-label="day.ariaLabel"
                    @click="selectDay(day)"
                >
                    <span class="propose-picker__day-name">{{ day.weekday }}</span>
                    <span class="propose-picker__day-number">{{ day.number }}</span>
                    <span v-if="day.hasPick" class="propose-picker__day-note propose-picker__day-check">
                        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
                            <circle cx="8" cy="8" r="8" fill="currentColor" />
                            <path d="M4.6 8.3l2.2 2.2 4.6-4.8" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </span>
                    <span v-else class="propose-picker__day-note">{{ day.note }}</span>
                </button>
            </div>

            <div class="propose-picker__times">
                <common-loader v-if="loading" :isProcessing="true" :width="56"></common-loader>
                <p v-else-if="loadError" class="propose-picker__message propose-picker__message--error">{{ loadError }}</p>
                <p v-else-if="!hasAnyOpenDay" class="propose-picker__message">
                    {{ $t('pages.referrals.pool.propose_picker.no_open_times') }}
                </p>
                <template v-else-if="selectedDay">
                    <h3 class="propose-picker__day-title">{{ selectedDay.longLabel }}</h3>
                    <div v-for="group in timeGroups" :key="group.key" class="propose-picker__group">
                        <div class="propose-picker__group-label">{{ group.label }}</div>
                        <div class="propose-picker__time-grid">
                            <button
                                v-for="time in group.times"
                                :key="time.key"
                                type="button"
                                :class="timeClasses(time)"
                                :disabled="time.isDisabled"
                                :aria-pressed="time.isSelected ? 'true' : 'false'"
                                :aria-label="time.ariaLabel"
                                @click="toggleTime(time)"
                            >
                                {{ time.label }}
                            </button>
                        </div>
                    </div>
                    <p v-if="!timeGroups.length" class="propose-picker__message">
                        {{ $t('pages.referrals.pool.propose_picker.no_times_this_day') }}
                    </p>
                </template>
            </div>
        </section>

        <aside class="propose-picker__tray" :aria-label="$t('pages.referrals.pool.propose_picker.your_picks')">
            <div class="propose-picker__tray-header">
                <h3>{{ $t('pages.referrals.pool.propose_picker.your_picks_count', { count: selectedSlots.length, max: maxSlots }) }}</h3>
                <button v-if="selectedSlots.length" type="button" class="propose-picker__link" @click="CLEAR_SELECTED">
                    {{ $t('pages.referrals.pool.propose_picker.clear_all') }}
                </button>
            </div>

            <ul class="propose-picker__picks">
                <li v-for="pick in picks" :key="pick.key" :class="['propose-picker__pick', { 'propose-picker__pick--empty': !pick.slot }]">
                    <template v-if="pick.slot">
                        <div>
                            <div class="propose-picker__pick-time">{{ pick.time }}</div>
                            <div class="propose-picker__pick-day">{{ pick.day }}</div>
                        </div>
                        <button
                            type="button"
                            class="propose-picker__pick-remove"
                            :aria-label="$t('pages.referrals.pool.propose_picker.remove', { slot: `${pick.day} ${pick.time}` })"
                            @click="TOGGLE_SLOT(pick.slot)"
                        >
                            ×
                        </button>
                    </template>
                    <template v-else>
                        {{ $t('pages.referrals.pool.propose_picker.pick_placeholder', { number: pick.number }) }}
                    </template>
                </li>
            </ul>

            <p v-if="hasSameDayPicks" class="propose-picker__tip">{{ $t('pages.referrals.pool.propose_picker.same_day_tip') }}</p>

            <common-button
                :label="sendLabel"
                :isSubmittable="false"
                :isDisabled="remaining > 0"
                :isProcessing="isSending"
                :isFluid="true"
                size="medium"
                type="primary"
                @click="$emit('send')"
            ></common-button>
            <p class="propose-picker__hint">{{ $t('pages.referrals.pool.propose_picker.patient_picks_one') }}</p>
        </aside>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import { mapGetters, mapMutations } from 'vuex'
    import UsersAPI from 'api/UsersAPI'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'

    // Days fetched per request, and how far ahead a provider can page.
    const LOAD_DAYS = 42
    const MAX_WEEKS = 12

    function dayKey(date) {
        return dayjs(date).startOf('day').format()
    }

    function slotKey(slot) {
        return `${slot.date}|${slot.startTime}|${slot.endTime}`
    }

    // A slot can be proposed when nobody holds it, it is not blocked, and it has not started yet.
    function isOpenSlot(slot, day) {
        if (slot.referral || slot.isUnavailable) return false
        return dayjs(`${day.format('YYYY-MM-DD')} ${slot.startTime}`).isAfter(dayjs())
    }

    function isBookedSlot(slot) {
        return (
            !!slot.referral &&
            slot.referral.status !== ReferralStatusEnum.CANCELLED &&
            slot.referral.status !== ReferralStatusEnum.NO_SHOW
        )
    }

    export default {
        props: {
            userId: { type: Number, default: undefined },
            practiceId: { type: Number, default: undefined },
            practiceLocationId: { type: Number, default: 0 },
            isSending: { type: Boolean, default: false },
        },

        data() {
            return {
                today: dayjs().startOf('day'),
                dates: {},
                loadedDays: 0,
                loading: false,
                loadError: '',
                weekIndex: 0,
                selectedKey: null,
            }
        },

        computed: {
            ...mapGetters('availability', ['selectedSlots', 'maxSlots']),

            maxWeeks() {
                return MAX_WEEKS
            },

            weekStart() {
                return this.today.add(this.weekIndex * 7, 'day')
            },

            weekLabel() {
                const end = this.weekStart.add(6, 'day')
                return `${this.weekStart.format('MMM D')} – ${end.format('MMM D')}`
            },

            pickedDayKeys() {
                return this.selectedSlots.map((slot) => dayKey(slot.date))
            },

            weekDays() {
                return Array.from({ length: 7 }, (_, i) => this.buildDay(this.weekStart.add(i, 'day')))
            },

            firstOpenKey() {
                for (let i = 0; i < this.loadedDays; i++) {
                    const day = this.today.add(i, 'day')
                    if (this.openSlotsFor(day).length) return dayKey(day)
                }
                return null
            },

            hasAnyOpenDay() {
                return !!this.firstOpenKey || this.loadedDays < MAX_WEEKS * 7
            },

            selectedDay() {
                if (!this.selectedKey) return null
                return this.buildDay(dayjs(this.selectedKey))
            },

            timeGroups() {
                if (!this.selectedDay) return []
                const day = dayjs(this.selectedKey)
                const slots = (this.dates[this.selectedKey] || [])
                    .filter((slot) => isOpenSlot(slot, day) || (isBookedSlot(slot) && dayjs(`${day.format('YYYY-MM-DD')} ${slot.startTime}`).isAfter(dayjs())))
                    .slice()
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                const isFull = this.selectedSlots.length >= this.maxSlots

                const times = slots.map((slot) => {
                    const isSelected = this.isSelected(slot)
                    const isBooked = !isOpenSlot(slot, day)
                    const label = this.formatTime(slot.startTime)
                    return {
                        key: slotKey(slot),
                        slot,
                        hour: parseInt(slot.startTime),
                        label: isBooked ? this.$t('pages.referrals.pool.propose_picker.booked', { time: label }) : label,
                        isSelected,
                        isBooked,
                        isDisabled: isBooked || (isFull && !isSelected),
                        ariaLabel: `${this.selectedDay.longLabel}, ${label}${isBooked ? ', ' + this.$t('pages.referrals.pool.propose_picker.booked_short') : ''}`,
                    }
                })

                return [
                    {
                        key: 'morning',
                        label: this.$t('pages.referrals.pool.propose_picker.morning'),
                        times: times.filter((t) => t.hour < 12),
                    },
                    {
                        key: 'afternoon',
                        label: this.$t('pages.referrals.pool.propose_picker.afternoon'),
                        times: times.filter((t) => t.hour >= 12),
                    },
                ].filter((group) => group.times.length)
            },

            picks() {
                const sorted = this.selectedSlots
                    .slice()
                    .sort((a, b) => dayKey(a.date).localeCompare(dayKey(b.date)) || a.startTime.localeCompare(b.startTime))
                return Array.from({ length: this.maxSlots }, (_, i) => {
                    const slot = sorted[i]
                    if (!slot) return { key: `empty-${i}`, number: i + 1 }
                    return {
                        key: slotKey(slot),
                        slot,
                        time: `${this.formatTime(slot.startTime)} – ${this.formatTime(slot.endTime)}`,
                        day: dayjs(slot.date).format('ddd, MMM D'),
                    }
                })
            },

            hasSameDayPicks() {
                return new Set(this.pickedDayKeys).size < this.pickedDayKeys.length
            },

            remaining() {
                return Math.max(0, this.maxSlots - this.selectedSlots.length)
            },

            sendLabel() {
                if (this.remaining > 0) {
                    return this.$tc('pages.referrals.pool.propose_picker.pick_more', this.remaining, { count: this.remaining })
                }
                return this.$t('pages.referrals.pool.propose_picker.send', { count: this.maxSlots })
            },
        },

        methods: {
            ...mapMutations('availability', ['TOGGLE_SLOT', 'CLEAR_SELECTED']),

            openSlotsFor(day) {
                return (this.dates[dayKey(day)] || []).filter((slot) => isOpenSlot(slot, day))
            },

            buildDay(day) {
                const key = dayKey(day)
                const openCount = this.openSlotsFor(day).length
                const isOpen = openCount > 0
                const hasPick = this.pickedDayKeys.includes(key)
                let note = this.$t('pages.referrals.pool.propose_picker.day_closed')
                if (hasPick) note = this.$t('pages.referrals.pool.propose_picker.day_picked')
                else if (isOpen) note = this.$tc('pages.referrals.pool.propose_picker.day_open', openCount, { count: openCount })

                return {
                    key,
                    weekday: day.format('ddd'),
                    number: day.format('D'),
                    longLabel: day.format('dddd, MMMM D'),
                    note,
                    isOpen,
                    hasPick,
                    isSelected: key === this.selectedKey,
                    ariaLabel: `${day.format('dddd, MMMM D')}, ${note}`,
                }
            },

            dayClasses(day) {
                return {
                    'propose-picker__day': true,
                    'propose-picker__day--selected': day.isSelected,
                    'propose-picker__day--picked': day.hasPick,
                    'propose-picker__day--closed': !day.isOpen,
                }
            },

            timeClasses(time) {
                return {
                    'propose-picker__time': true,
                    'propose-picker__time--selected': time.isSelected,
                    'propose-picker__time--booked': time.isBooked,
                }
            },

            isSelected(slot) {
                const key = slotKey(slot)
                return this.selectedSlots.some((s) => slotKey(s) === key)
            },

            formatTime(time) {
                return dayjs(`2000-01-01 ${time}`).format('h:mm A')
            },

            selectDay(day) {
                if (day.isOpen) this.selectedKey = day.key
            },

            toggleTime(time) {
                if (!time.isDisabled || time.isSelected) this.TOGGLE_SLOT(time.slot)
            },

            changeWeek(step) {
                this.weekIndex = Math.min(Math.max(0, this.weekIndex + step), MAX_WEEKS - 1)
                const lastVisible = (this.weekIndex + 1) * 7
                if (lastVisible > this.loadedDays) this.loadMore()
                // Keep a selected day in view: move to the first open day of the new week.
                if (!this.weekDays.some((d) => d.isSelected)) {
                    const open = this.weekDays.find((d) => d.isOpen)
                    if (open) this.selectedKey = open.key
                }
            },

            loadMore() {
                if (!this.userId || !this.practiceId || this.loading || this.loadedDays >= MAX_WEEKS * 7) {
                    return Promise.resolve()
                }

                const from = this.today.add(this.loadedDays, 'day')
                const to = from.add(LOAD_DAYS - 1, 'day')
                this.loading = true
                this.loadError = ''

                return UsersAPI.getSchedule(
                    this.userId,
                    this.practiceId,
                    from.format('YYYY-MM-DD'),
                    to.format('YYYY-MM-DD'),
                    this.practiceLocationId,
                )
                    .then((res) => {
                        this.dates = { ...this.dates, ...res }
                        this.loadedDays += LOAD_DAYS
                        if (!this.selectedKey) this.openFirstAvailable()
                    })
                    .catch(() => {
                        this.loadError = this.$t('elements.availability.schedule_load_error')
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },

            // Start where the provider can act: the week holding the first open day.
            openFirstAvailable() {
                if (!this.firstOpenKey) {
                    if (this.loadedDays < MAX_WEEKS * 7) this.loadMore()
                    return
                }
                this.selectedKey = this.firstOpenKey
                this.weekIndex = Math.floor(dayjs(this.firstOpenKey).diff(this.today, 'day') / 7)
            },

            reset() {
                this.dates = {}
                this.loadedDays = 0
                this.weekIndex = 0
                this.selectedKey = null
                this.loadMore()
            },
        },

        watch: {
            userId() {
                this.reset()
            },
            practiceId() {
                this.reset()
            },
            practiceLocationId() {
                this.reset()
            },
        },

        mounted() {
            this.loadMore()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .propose-picker {
        display: flex;
        flex-wrap: wrap;
        gap: rem(16px);
        align-items: flex-start;

        // The base stylesheet strips button padding and alignment; picker buttons centre their labels.
        button {
            font-family: inherit;
            text-align: center;
        }

        &__week-btn,
        &__time,
        &__pick-remove {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 rem(8px);
        }

        &__calendar,
        &__tray {
            background: #fff;
            border: 1px solid $color--border-base;
            border-radius: rem(12px);
            box-sizing: border-box;
        }

        &__calendar {
            flex: 999 1 rem(560px);
            min-width: 0;
            padding: rem(20px);
            display: flex;
            flex-direction: column;
            gap: rem(16px);
        }

        &__week {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: rem(12px);
        }

        &__week-label {
            font-weight: 600;
            text-align: center;
            color: $color--accent-3;
        }

        &__week-btn {
            width: rem(44px);
            height: rem(44px);
            border: 1px solid $color--border-base;
            border-radius: rem(10px);
            background: #fff;
            color: $color--accent-3;
            font-size: rem(22px);
            line-height: 1;
            cursor: pointer;

            &:disabled {
                color: #c2cad4;
                cursor: default;
            }
        }

        &__days {
            display: grid;
            grid-template-columns: repeat(7, minmax(0, 1fr));
            gap: rem(6px);
        }

        &__day {
            min-height: rem(72px);
            padding: rem(6px) rem(2px);
            border: 1px solid $color--border-base;
            border-radius: rem(10px);
            background: #fff;
            color: $color--accent-3;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: rem(1px);
            cursor: pointer;

            &--selected {
                border: 2px solid $color--accent-1;
                background: #eef9fd;
            }

            &--closed {
                border-color: #eef2f5;
                background: #f6f8fa;
                color: #8a96a5;
                cursor: default;
            }
        }

        &__day-name {
            font-size: rem(11px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        &__day-number {
            font-size: rem(18px);
            font-weight: 600;
            line-height: 1.2;
        }

        &__day-note {
            font-size: rem(10px);
            min-height: rem(13px);
        }

        &__day-check {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: $color--accent-1;
        }

        &__times {
            position: relative;
            min-height: rem(120px);
            border-top: 1px solid #eef2f5;
            padding-top: rem(16px);
            display: flex;
            flex-direction: column;
            gap: rem(14px);
        }

        &__day-title {
            margin: 0;
            font-size: rem(15px);
            font-weight: 600;
            color: $color--accent-3;
        }

        &__group {
            display: flex;
            flex-direction: column;
            gap: rem(8px);
        }

        &__group-label {
            font-size: rem(12px);
            font-weight: 500;
            color: #4a5a78;
        }

        &__time-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(rem(112px), 1fr));
            gap: rem(8px);
        }

        &__time {
            min-height: rem(44px);
            border: 1px solid #c9d3dd;
            border-radius: rem(8px);
            background: #fff;
            color: $color--accent-3;
            font-size: rem(14px);
            font-weight: 500;
            cursor: pointer;

            &:disabled {
                color: #a5b0bd;
                border-color: #eef2f5;
                cursor: default;
            }

            &--selected,
            &--selected:disabled {
                border-color: $color--accent-1;
                background: $color--accent-1;
                color: #fff;
                cursor: pointer;
            }

            &--booked {
                background: #f6f8fa;
                text-decoration: line-through;
            }
        }

        &__message {
            margin: 0;
            font-size: rem(13px);
            color: #4a5a78;

            &--error {
                color: $color--accent-4;
            }
        }

        &__tray {
            flex: 1 1 rem(300px);
            position: sticky;
            top: rem(16px);
            padding: rem(18px);
            display: flex;
            flex-direction: column;
            gap: rem(12px);

            // On a phone the tray is pinned to the bottom, so it stays short: picks as a
            // compact wrap of chips and no empty placeholders or hint.
            @include breakpoint(tablet-small) {
                top: auto;
                bottom: 0;
                padding: rem(12px) rem(14px);
                gap: rem(8px);
                border-radius: rem(16px) rem(16px) 0 0;
                box-shadow: 0 -6px 24px rgba(0, 29, 80, 0.12);
            }
        }

        &__tray-header {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: rem(8px);

            h3 {
                margin: 0;
                font-size: rem(15px);
                font-weight: 600;
                color: $color--accent-3;
            }
        }

        &__link {
            border: 0;
            background: none;
            padding: rem(6px) 0;
            color: #0b7fa6;
            font-size: rem(12px);
            font-weight: 500;
            cursor: pointer;
        }

        &__picks {
            margin: 0;
            padding: 0;
            list-style: none;
            display: flex;
            flex-direction: column;
            gap: rem(8px);
        }

        &__pick {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: rem(8px);
            padding: rem(8px) rem(8px) rem(8px) rem(12px);
            border: 1px solid $color--accent-1;
            border-radius: rem(10px);
            background: #eef9fd;

            &--empty {
                padding: rem(12px);
                border: 1px dashed #c9d3dd;
                background: #fff;
                color: #6b7a90;
                font-size: rem(13px);
            }
        }

        &__pick-time {
            font-weight: 600;
            color: $color--accent-3;
        }

        &__pick-day {
            font-size: rem(12px);
            color: #4a5a78;
        }

        &__pick-remove {
            width: rem(36px);
            height: rem(36px);
            border: 0;
            border-radius: rem(8px);
            background: transparent;
            color: #4a5a78;
            font-size: rem(18px);
            cursor: pointer;
        }

        &__tip {
            margin: 0;
            padding: rem(8px) rem(10px);
            border-radius: rem(8px);
            background: #fff7e0;
            color: #7a4b00;
            font-size: rem(12px);
        }

        &__hint {
            margin: 0;
            font-size: rem(12px);
            color: #4a5a78;
        }

        @include breakpoint(tablet-small) {
            &__picks {
                flex-direction: row;
                flex-wrap: wrap;
                gap: rem(6px);
            }

            &__pick {
                padding: rem(2px) rem(2px) rem(2px) rem(8px);
                font-size: rem(12px);

                &--empty {
                    display: none;
                }
            }

            &__pick-time {
                font-size: rem(12px);
            }

            &__pick-day {
                font-size: rem(11px);
            }

            &__pick-remove {
                width: rem(32px);
                height: rem(32px);
            }

            &__hint {
                display: none;
            }
        }
    }
</style>
